import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import SEOHead from './SEOHead';
import NewTopBar from './NewTopBar';
import NewHeader from './NewHeader';
import NewFooter from './NewFooter';

const PrivacyPolicy = () => {
  const { language } = useLanguage();
  const sl = language === 'sl';

  const content = sl ? {
    title: 'Politika zasebnosti',
    metaDesc: 'Politika zasebnosti spletne strani GENERAL LEDGER d.o.o. — računovodski servis, Rogatec, Slovenija.',
    lastUpdated: 'Zadnja posodobitev: januar 2026',
    intro: 'Podjetje GENERAL LEDGER d.o.o. (v nadaljevanju: „mi", „nas", „naš") spoštuje vašo zasebnost in se zavezuje k varovanju vaših osebnih podatkov. Ta politika zasebnosti pojasnjuje, kako zbiramo, uporabljamo in varujemo vaše osebne podatke.',
    sections: [
      {
        title: '1. Upravljavec osebnih podatkov',
        content: `Upravljavec osebnih podatkov je:
        
GENERAL LEDGER d.o.o.
Celjska cesta 47
3252 Rogatec, Slovenija

E-pošta: info@general-ledger.eu
Telefon: +386 3 818 40 60`
      },
      {
        title: '2. Katere podatke zbiramo',
        content: `Zbiramo naslednje osebne podatke:

• **Kontaktni podatki:** ime, priimek, e-pošta, telefonska številka
• **Podatki o podjetju:** ime podjetja, davčna številka (če jo posredujete)
• **Komunikacija:** vsebina vaših sporočil, vključno s klepetom z AI asistentom
• **Tehnični podatki:** IP naslov, tip brskalnika (za zagotavljanje varnosti in preprečevanje zlorab)`
      },
      {
        title: '3. Namen obdelave',
        content: `Vaše osebne podatke obdelujemo za naslednje namene:

• **Odgovarjanje na povpraševanja:** za obdelavo vaših poizvedb in rezervacij brezplačnih posvetov
• **Izvajanje storitev:** za zagotavljanje računovodskih, davčnih in drugih storitev
• **Komunikacija:** za pošiljanje odgovorov na vaša vprašanja
• **Varnost:** za preprečevanje zlorab in zaščito naših sistemov`
      },
      {
        title: '4. Pravna podlaga',
        content: `Vaše osebne podatke obdelujemo na naslednjih pravnih podlagah:

• **Privolitev (člen 6(1)(a) GDPR):** kadar oddate obrazec za brezplačen posvet ali uporabite klepet
• **Zakoniti interes (člen 6(1)(f) GDPR):** za zagotavljanje varnosti naših sistemov`
      },
      {
        title: '5. Hramba podatkov',
        content: `Vaše osebne podatke hranimo le toliko časa, kolikor je potrebno za namene, za katere so bili zbrani:

• **Povpraševanja:** 2 leti od zadnjega kontakta
• **Prepisi klepetov:** 1 leto
• **Podatki o strankah:** v skladu z zakonodajo o računovodstvu (10 let)`
      },
      {
        title: '6. Vaše pravice',
        content: `V skladu z GDPR imate naslednje pravice:

• **Pravica do dostopa:** zahtevate lahko dostop do vaših osebnih podatkov
• **Pravica do popravka:** zahtevate lahko popravek netočnih podatkov
• **Pravica do izbrisa:** zahtevate lahko izbris vaših podatkov („pravica do pozabe")
• **Pravica do omejitve obdelave:** zahtevate lahko omejitev obdelave vaših podatkov
• **Pravica do prenosljivosti:** zahtevate lahko prenos podatkov v strukturirani obliki
• **Pravica do ugovora:** ugovarjate lahko obdelavi vaših podatkov

Za uveljavljanje svojih pravic nas kontaktirajte na info@general-ledger.eu.`
      },
      {
        title: '7. Piškotki',
        content: `Naša spletna stran uporablja le nujne piškotke za delovanje strani. Ne uporabljamo piškotkov za sledenje ali oglaševanje.

• **Nujni piškotki:** omogočajo osnovno delovanje spletne strani (npr. ohranjanje jezikovnih nastavitev)`
      },
      {
        title: '8. AI asistent',
        content: `Naša spletna stran vključuje AI asistenta, ki vam pomaga pri odgovarjanju na vprašanja. AI asistent:

• Ne hrani osebnih podatkov trajno
• Uporablja vaša sporočila le za generiranje odgovorov
• Prepisi pogovorov se hranijo le, če jih pošljete naši ekipi

AI asistent lahko občasno poda netočne informacije. Za pravno zavezujoče informacije se obrnite na našo ekipo.`
      },
      {
        title: '9. Spremembe politike',
        content: 'To politiko zasebnosti lahko občasno posodobimo. Spremembe bodo objavljene na tej strani z navedenim datumom zadnje posodobitve.'
      },
      {
        title: '10. Kontakt',
        content: `Za vprašanja glede zasebnosti nas kontaktirajte:

E-pošta: info@general-ledger.eu
Telefon: +386 3 818 40 60
Naslov: Celjska cesta 47, 3252 Rogatec, Slovenija

Če menite, da smo kršili vaše pravice, imate pravico vložiti pritožbo pri Informacijskem pooblaščencu RS (ip-rs.si).`
      }
    ]
  } : {
    title: 'Privacy Policy',
    metaDesc: 'Privacy Policy of GENERAL LEDGER d.o.o. website — accounting firm, Rogatec, Slovenia.',
    lastUpdated: 'Last updated: January 2026',
    intro: 'GENERAL LEDGER d.o.o. (hereinafter: "we", "us", "our") respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use and protect your personal data.',
    sections: [
      {
        title: '1. Data Controller',
        content: `The data controller is:
        
GENERAL LEDGER d.o.o.
Celjska cesta 47
3252 Rogatec, Slovenia

Email: info@general-ledger.eu
Phone: +386 3 818 40 60`
      },
      {
        title: '2. Data We Collect',
        content: `We collect the following personal data:

• **Contact information:** name, surname, email, phone number
• **Company data:** company name, tax number (if provided)
• **Communication:** content of your messages, including chat with AI assistant
• **Technical data:** IP address, browser type (for security and abuse prevention)`
      },
      {
        title: '3. Purpose of Processing',
        content: `We process your personal data for the following purposes:

• **Responding to inquiries:** to process your queries and free consultation bookings
• **Service delivery:** to provide accounting, tax and other services
• **Communication:** to send responses to your questions
• **Security:** to prevent abuse and protect our systems`
      },
      {
        title: '4. Legal Basis',
        content: `We process your personal data on the following legal bases:

• **Consent (Article 6(1)(a) GDPR):** when you submit a consultation form or use the chat
• **Legitimate interest (Article 6(1)(f) GDPR):** to ensure the security of our systems`
      },
      {
        title: '5. Data Retention',
        content: `We retain your personal data only as long as necessary for the purposes for which they were collected:

• **Inquiries:** 2 years from last contact
• **Chat transcripts:** 1 year
• **Client data:** in accordance with accounting legislation (10 years)`
      },
      {
        title: '6. Your Rights',
        content: `Under GDPR, you have the following rights:

• **Right of access:** you can request access to your personal data
• **Right to rectification:** you can request correction of inaccurate data
• **Right to erasure:** you can request deletion of your data ("right to be forgotten")
• **Right to restriction:** you can request restriction of processing of your data
• **Right to portability:** you can request transfer of data in a structured format
• **Right to object:** you can object to processing of your data

To exercise your rights, contact us at info@general-ledger.eu.`
      },
      {
        title: '7. Cookies',
        content: `Our website uses only essential cookies for the operation of the site. We do not use tracking or advertising cookies.

• **Essential cookies:** enable basic website functionality (e.g., maintaining language settings)`
      },
      {
        title: '8. AI Assistant',
        content: `Our website includes an AI assistant that helps answer your questions. The AI assistant:

• Does not permanently store personal data
• Uses your messages only to generate responses
• Chat transcripts are stored only if you send them to our team

The AI assistant may occasionally provide inaccurate information. For legally binding information, contact our team.`
      },
      {
        title: '9. Policy Changes',
        content: 'We may update this privacy policy from time to time. Changes will be posted on this page with the last updated date.'
      },
      {
        title: '10. Contact',
        content: `For privacy questions, contact us:

Email: info@general-ledger.eu
Phone: +386 3 818 40 60
Address: Celjska cesta 47, 3252 Rogatec, Slovenia

If you believe we have violated your rights, you have the right to file a complaint with the Information Commissioner of the Republic of Slovenia (ip-rs.si).`
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#F6F2EA]">
      <SEOHead
        title={`${content.title} — General Ledger d.o.o.`}
        description={content.metaDesc}
        canonicalPath={sl ? '/zasebnost' : '/privacy'}
        lang={sl ? 'sl' : 'en'}
        alternateUrl={sl ? '/privacy' : '/zasebnost'}
      />
      <NewTopBar />
      <NewHeader />
      
      <main className="max-w-[900px] mx-auto px-6 py-16 lg:py-24">
        <div className="mb-10">
          <span className="text-[13px] font-bold tracking-[2.5px] text-[#7A5B28] block mb-4">
            {sl ? 'PRAVNE INFORMACIJE' : 'LEGAL INFORMATION'}
          </span>
          <h1 className="font-serif text-[40px] lg:text-[56px] leading-[1.05] tracking-[-1px] text-[#15181C]">
            {content.title}
          </h1>
          <p className="text-[14px] text-[#7A5B28] mt-4">{content.lastUpdated}</p>
        </div>

        <p className="text-[17px] text-[#4E5359] leading-[1.7] mb-10">
          {content.intro}
        </p>

        <div className="space-y-10">
          {content.sections.map((section) => (
            <div key={section.title} className="border-t border-[#E2DACB] pt-8">
              <h2 className="font-serif text-[24px] lg:text-[28px] text-[#15181C] mb-4">
                {section.title}
              </h2>
              <div className="text-[16px] text-[#4E5359] leading-[1.7] whitespace-pre-line">
                {section.content}
              </div>
            </div>
          ))}
          
          {/* Additional SEO content */}
          <div className="border-t border-[#E2DACB] pt-8 mt-8">
            <p className="text-[14px] text-[#7E786D] leading-[1.8]">
              {sl 
                ? 'GENERAL LEDGER d.o.o. je računovodski servis s sedežem v Rogatcu, Slovenija. Od leta 2003 nudimo storitve ustanovitve podjetij (d.o.o., s.p., podružnice), računovodstva in DDV obračunov, pridobivanja delovnih dovoljenj, obračuna plač ter davčnega svetovanja. Naša ekipa govori slovensko, angleško, srbohrvaško in špansko. Za vprašanja o varstvu osebnih podatkov nas kontaktirajte na info@general-ledger.eu ali +386 3 818 40 60.'
                : 'GENERAL LEDGER d.o.o. is an accounting firm based in Rogatec, Slovenia. Since 2003, we have been providing services including company formation (d.o.o., s.p., branches), accounting and VAT returns, work permit applications, payroll processing, and tax advisory services. Our team speaks Slovenian, English, Serbo-Croatian, and Spanish. For questions about data protection, contact us at info@general-ledger.eu or +386 3 818 40 60.'}
            </p>
          </div>
        </div>
      </main>

      <NewFooter />
    </div>
  );
};

export default PrivacyPolicy;
